import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { addProduct, updateProduct } from '../services/productService';
import { getCategories, addCategory } from '../../companysettings/services/productCategoryService';
import { getSuppliers, addSupplier } from '../../suppliers/services/supplierService';
import { getProductBrands, addProductBrand } from '../../productbrand/services/productBrandService';
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

const AddProductPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);

  const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${timestamp}${random}`;
  };
  
  const navigationData = location.state?.productData;
  
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  
  const [originalFormData, setOriginalFormData] = useState({});
  const [hasFormChanged, setHasFormChanged] = useState(false);
  
  // Form state - ✅ Removed VAT-related fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    imageUrl: '',
    categoryId: '',
    price: '',
    minSalePrice: '',
    costPrice: '',
    averagePrice: '',
    stockQuantity: '',
    minStockLevel: '',
    supplierId: '',
    productBrandId: '',
    modelNumber: '',
    isActive: true,
    isPromotionProduct: false,
    promotionDiscount: '',
    syncToShopify: false,
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(''); // Server-level error message

  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);

  const pendingCategoryNameRef = useRef('');

  const checkIfFormChanged = (currentData, originalData) => {
    const currentStr = JSON.stringify(currentData);
    const originalStr = JSON.stringify(originalData);
    return currentStr !== originalStr;
  };

  const populateFormData = (product) => {
    const populatedData = {
      name: product.Name || product.ProductName || product.name || '',
      description: product.Description || product.description || '',
      sku: product.SKU || product.Sku || product.sku || '',
      barcode: product.Barcode || product.BarCode || product.barcode || '',
      imageUrl: product.ImageUrl || product.imageUrl || '',
      categoryId: product.ProductCategoryId ? String(product.ProductCategoryId) : '',
      price: product.SalePrice ? String(product.SalePrice) : '',
      minSalePrice: product.MinSalePrice ? String(product.MinSalePrice) : '',
      costPrice: product.CostPrice ? String(product.CostPrice) : '',
      averagePrice: product.AveragePrice ? String(product.AveragePrice) : '',
      stockQuantity: product.StockQuantity || product.StockQuanity ? String(product.StockQuantity || product.StockQuanity) : '',
      minStockLevel: product.MinStockLevel ? String(product.MinStockLevel) : '',
      supplierId: product.SupplierId ? String(product.SupplierId) : '',
      productBrandId: product.ProductBrandId ? String(product.ProductBrandId) : (product.BrandId ? String(product.BrandId) : ''),
      modelNumber: product.ModelNumber || '',
      isActive: product.IsActive !== undefined ? product.IsActive : true,
      isPromotionProduct: product.IsPromotionProduct || product.isPromotionProduct || false,
      promotionDiscount: product.PromotionPercentage ? String(product.PromotionPercentage) : '',
      syncToShopify: product.SyncToShopify || false
    };
    
    // Store category name for fallback lookup once categories are loaded
    pendingCategoryNameRef.current =
      product.ProductCategory?.CategoryName || product.CategoryName || '';

    setFormData(populatedData);
    if (isEditMode) {
      setOriginalFormData({ ...populatedData });
      setHasFormChanged(false);
    }
  };

  const loadProductDataFromAPI = async () => {
    if (!id) {
      toast.error('Product ID is missing');
      navigate('/products');
      return;
    }

    setDataLoading(true);
    try {
      const response = await axiosInstance.get(`Products/${id}`);
      
      if (response.data.status === 'Success' && response.data.payload) {
        populateFormData(response.data.payload);
      } else {
        throw new Error(response.data.message || 'Product not found');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Product not found');
      } else {
        toast.error('Failed to load product data');
      }
      navigate('/products');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode) return;

    if (navigationData) {
      populateFormData(navigationData);
    } else {
      loadProductDataFromAPI();
    }
  }, [isEditMode, navigationData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        try {
          const categoriesResponse = await getCategories();
          const categoryData = categoriesResponse?.payload || categoriesResponse || [];
          setCategories(Array.isArray(categoryData) ? categoryData : []);
        } catch (catError) {
          console.error('Error fetching categories:', catError);
          setCategories([]);
        }

        try {
          const suppliersResponse = await getSuppliers();
          const supplierData = suppliersResponse?.payload || suppliersResponse || [];
          setSuppliers(Array.isArray(supplierData) ? supplierData : []);
        } catch (suppError) {
          console.error('Error fetching suppliers:', suppError);
          setSuppliers([]);
        }

        try {
          const brandsResponse = await getProductBrands();
          const brandData = brandsResponse?.payload || brandsResponse || [];
          setBrands(Array.isArray(brandData) ? brandData : []);
        } catch (brandError) {
          console.error('Error fetching brands:', brandError);
          setBrands([]);
        }
        
      } catch (error) {
        console.error('Critical error during data fetch:', error);
        setCategories([]);
        setSuppliers([]);
        setBrands([]);
      }
    };

    fetchData();
  }, []);

  // Fallback: if category wasn't set by ID, find it by CategoryName once categories load
  useEffect(() => {
    if (!isEditMode) return;
    if (!categories.length) return;
    if (formData.categoryId) return;
    const name = pendingCategoryNameRef.current;
    if (!name) return;
    const match = categories.find(c => c.CategoryName === name);
    if (match) {
      const idStr = String(match.Id);
      setFormData(prev => ({ ...prev, categoryId: idStr }));
      setOriginalFormData(prev => ({ ...prev, categoryId: idStr }));
    }
  }, [categories]);

  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const calculateFormValidity = () => {
    // Required fields
    if (!formData.name.trim()) return false;
    if (formData.name.length > 300) return false; // ✅ Updated limit
    
    if (!formData.categoryId) return false;
    if (!formData.supplierId) return false;
    if (!formData.productBrandId) return false;
    
    if (!formData.price || parseFloat(formData.price) < 0 || parseFloat(formData.price) > 100000) return false;
    if (!formData.costPrice || parseFloat(formData.costPrice) < 0 || parseFloat(formData.costPrice) > 100000) return false;
    if (!formData.stockQuantity || parseFloat(formData.stockQuantity) < 0 || parseFloat(formData.stockQuantity) > 100000) return false;
    
    // Optional fields validation
    if (formData.sku && (formData.sku.length < 3 || formData.sku.length > 30)) return false;
    if (formData.barcode && (formData.barcode.length < 3 || formData.barcode.length > 30)) return false;
    if (formData.description && formData.description.length > 500) return false; // ✅ Updated limit
    if (formData.modelNumber && formData.modelNumber.length > 200) return false; // ✅ Updated limit
    
    if (formData.imageUrl) {
      if (formData.imageUrl.length > 300) return false;
      try {
        new URL(formData.imageUrl);
      } catch {
        return false;
      }
    }
    
    if (formData.minSalePrice) {
      const minPrice = parseFloat(formData.minSalePrice);
      if (minPrice < 0 || minPrice > 100000) return false;
    }
    
    if (formData.minStockLevel) {
      const minStock = parseFloat(formData.minStockLevel);
      if (minStock < 0 || minStock > 100000) return false;
    }
    
    if (formData.isPromotionProduct) {
      const discount = parseFloat(formData.promotionDiscount);
      if (!formData.promotionDiscount || discount < 0 || discount > 100) return false;
    }
    
    return true;
  };

  useEffect(() => {
    const isValid = calculateFormValidity();
    setIsFormValid(isValid);
  }, [
    formData.name,
    formData.sku,
    formData.categoryId,
    formData.price,
    formData.costPrice,
    formData.stockQuantity,
    formData.minStockLevel,
    formData.description,
    formData.modelNumber,
    formData.isPromotionProduct,
    formData.promotionDiscount
  ]);

  const validateAndShowErrors = () => {
    const newErrors = {};

    // Name validation - ✅ Updated limit
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    } else if (formData.name.length > 300) {
      newErrors.name = 'Name cannot exceed 300 characters';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!formData.supplierId) {
      newErrors.supplierId = 'Supplier is required';
    }

    if (!formData.price) {
      newErrors.price = 'Sale price is required';
    } else {
      const price = parseFloat(formData.price);
      if (price < 0 || price > 100000) {
        newErrors.price = 'Sale price must be between 0 and 100,000';
      }
    }

    if (!formData.costPrice) {
      newErrors.costPrice = 'Cost price is required';
    } else {
      const costPrice = parseFloat(formData.costPrice);
      if (costPrice < 0 || costPrice > 100000) {
        newErrors.costPrice = 'Cost price must be between 0 and 100,000';
      }
    }

    if (!formData.stockQuantity) {
      newErrors.stockQuantity = 'Stock quantity is required';
    } else {
      const stock = parseFloat(formData.stockQuantity);
      if (stock < 0 || stock > 100000) {
        newErrors.stockQuantity = 'Stock quantity must be between 0 and 100,000';
      }
    }

    if (formData.sku && (formData.sku.length < 3 || formData.sku.length > 30)) {
      newErrors.sku = 'SKU must be between 3-30 characters';
    }

    if (formData.barcode && (formData.barcode.length < 3 || formData.barcode.length > 30)) {
      newErrors.barcode = 'Barcode must be between 3-30 characters';
    }

    // ✅ Updated description limit
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    // ✅ Updated model number limit
    if (formData.modelNumber && formData.modelNumber.length > 200) {
      newErrors.modelNumber = 'Model number cannot exceed 200 characters';
    }

    if (formData.imageUrl) {
      if (formData.imageUrl.length > 300) {
        newErrors.imageUrl = 'Image URL cannot exceed 300 characters';
      } else {
        try {
          const url = new URL(formData.imageUrl);
          if (!['http:', 'https:'].includes(url.protocol)) {
            newErrors.imageUrl = 'URL must start with http:// or https://';
          }
        } catch {
          newErrors.imageUrl = 'Please enter a valid URL (e.g., https://example.com/image.jpg)';
        }
      }
    }

    if (formData.minSalePrice) {
      const minPrice = parseFloat(formData.minSalePrice);
      if (minPrice < 0 || minPrice > 100000) {
        newErrors.minSalePrice = 'Min sale price must be between 0 and 100,000';
      }
    }

    if (formData.minStockLevel) {
      const minStock = parseFloat(formData.minStockLevel);
      if (minStock < 0 || minStock > 100000) {
        newErrors.minStockLevel = 'Min stock level must be between 0 and 100,000';
      }
    }

    if (formData.isPromotionProduct) {
      const discount = parseFloat(formData.promotionDiscount);
      if (!formData.promotionDiscount || discount < 0 || discount > 100) {
        newErrors.promotionDiscount = 'Promotion percentage must be between 0-100%';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSingleField = (fieldName, value) => {
    if (!hasAttemptedSubmit) return;

    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          error = 'Product name is required';
        } else if (value.length > 300) { // ✅ Updated limit
          error = 'Name cannot exceed 300 characters';
        }
        break;
      case 'categoryId':
        if (!value) {
          error = 'Category is required';
        }
        break;
      case 'supplierId':
        if (!value) {
          error = 'Supplier is required';
        }
        break;
      case 'price':
        if (!value) {
          error = 'Sale price is required';
        } else {
          const price = parseFloat(value);
          if (price < 0 || price > 100000) {
            error = 'Sale price must be between 0 and 100,000';
          }
        }
        break;
      case 'costPrice':
        if (!value) {
          error = 'Cost price is required';
        } else {
          const costPrice = parseFloat(value);
          if (costPrice < 0 || costPrice > 100000) {
            error = 'Cost price must be between 0 and 100,000';
          }
        }
        break;
      case 'stockQuantity':
        if (!value) {
          error = 'Stock quantity is required';
        } else {
          const stock = parseFloat(value);
          if (stock < 0 || stock > 100000) {
            error = 'Stock quantity must be between 0 and 100,000';
          }
        }
        break;
      case 'sku':
        if (value && (value.length < 3 || value.length > 30)) {
          error = 'SKU must be between 3-30 characters';
        }
        break;
      case 'barcode':
        if (value && (value.length < 3 || value.length > 30)) {
          error = 'Barcode must be between 3-30 characters';
        }
        break;
      case 'description':
        if (value && value.length > 500) { // ✅ Updated limit
          error = 'Description cannot exceed 500 characters';
        }
        break;
      case 'imageUrl':
        if (value) {
          if (value.length > 300) {
            error = 'Image URL cannot exceed 300 characters';
          } else {
            try {
              const url = new URL(value);
              if (!['http:', 'https:'].includes(url.protocol)) {
                error = 'URL must start with http:// or https://';
              }
            } catch {
              error = 'Please enter a valid URL (e.g., https://example.com/image.jpg)';
            }
          }
        }
        break;
      case 'minSalePrice':
        if (value) {
          const minPrice = parseFloat(value);
          if (minPrice < 0 || minPrice > 100000) {
            error = 'Min sale price must be between 0 and 100,000';
          }
        }
        break;
      case 'minStockLevel':
        if (value) {
          const minStock = parseFloat(value);
          if (minStock < 0 || minStock > 100000) {
            error = 'Min stock level must be between 0 and 100,000';
          }
        }
        break;
      case 'promotionDiscount':
        if (formData.isPromotionProduct) {
          const discount = parseFloat(value);
          if (!value || discount < 0 || discount > 100) {
            error = 'Promotion percentage must be between 0-100%';
          }
        }
        break;
      case 'modelNumber':
        if (value && value.length > 200) { // ✅ Updated limit
          error = 'Model number cannot exceed 200 characters';
        }
        break;
      case 'averagePrice':
        if (value) {
          const avgPrice = parseFloat(value);
          if (avgPrice < 0 || avgPrice > 100000) {
            error = 'Average price must be between 0 and 100,000';
          }
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      if (isEditMode) {
        const formChanged = checkIfFormChanged(updated, originalFormData);
        setHasFormChanged(formChanged);
      }
      
      return updated;
    });
    
    if (errors[name] && touchedFields[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear server error when user makes changes
    if (serverError) {
      setServerError('');
    }
  };

  const handleFieldBlur = (event) => {
    const { name, value } = event.target;
    
    if (hasUserInteracted || hasAttemptedSubmit) {
      validateSingleField(name, value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setHasAttemptedSubmit(true);
    setServerError(''); // Clear any previous server errors
    
    if (!validateAndShowErrors()) {
      setServerError('Please fill in all required fields');
      
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    if (isEditMode && !hasFormChanged) {
      toast.warning('No changes detected. Please modify the form to update.');
      return;
    }

    setLoading(true);

    try {
      // ✅ Removed all VAT-related fields
      const productData = {
        Name: formData.name.trim(),
        SKU: formData.sku.trim() || null,
        BarCode: formData.barcode.trim() || null,
        Description: formData.description.trim() || null,
        ImageUrl: formData.imageUrl.trim() || null,
        SalePrice: parseFloat(formData.price),
        MinSalePrice: formData.minSalePrice ? parseFloat(formData.minSalePrice) : 0,
        CostPrice: parseFloat(formData.costPrice),
        StockQuantity: parseFloat(formData.stockQuantity),
        MinStockLevel: formData.minStockLevel ? parseFloat(formData.minStockLevel) : 0,
        IsPromotionProduct: formData.isPromotionProduct,
        PromotionPercentage: formData.isPromotionProduct 
          ? parseFloat(formData.promotionDiscount) 
          : 0,
        ProductCategoryId: parseInt(formData.categoryId),
        SupplierId: parseInt(formData.supplierId),
        ProductBrandId: formData.productBrandId ? parseInt(formData.productBrandId) : null,
        ModelNumber: formData.modelNumber?.trim() || null,
        AveragePrice: formData.averagePrice ? parseFloat(formData.averagePrice) : 0,
        IsActive: formData.isActive,
        IsWishlistProduct: false,
        SyncToShopify: formData.syncToShopify
      };

      let response;
      if (isEditMode) {
        const updatePayload = {
          ...productData,
          Id: parseInt(id)
        };
        
        response = await updateProduct(updatePayload);
        toast.success('Product updated successfully!');
      } else {
        response = await addProduct(productData);
        toast.success('Product added successfully!');
      }
      
      navigate('/products', {
        state: { 
          message: isEditMode ? 'Product updated successfully' : 'Product added successfully',
          productId: response.Id || response.id || id
        }
      });

    } catch (error) {
      console.error('Error saving product:', error);
      
      // Display all backend errors at the bottom of the form, no toasts
      const errorMessage = error.response?.data?.message || '';
      
      if (errorMessage) {
        setServerError(errorMessage);
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setServerError('Please fix the form errors');
      } else {
        // Generic fallback error
        setServerError(isEditMode ? 'Failed to update product' : 'Failed to add product');
      }
      
      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.getElementById('server-error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    setFormData({ ...originalFormData });
    setHasFormChanged(false);
    setErrors({});
    setServerError('');
    setTouchedFields({});
    setHasUserInteracted(false);
    setHasAttemptedSubmit(false);
    toast.info('Form reset to original values');
  };

  const isButtonDisabled = useMemo(() => {
    return loading || (isEditMode && !hasFormChanged);
  }, [loading, isEditMode, hasFormChanged]);

  const shouldShowError = (fieldName) => {
    return hasAttemptedSubmit && !!errors[fieldName];
  };

  if (dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 400,
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading product data...
          </Typography>
        </Box>
      </Container>
    );
  }

  const handleCancel = () => {
    const hasAnyData = Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'boolean') return value !== true;
      return value !== '';
    });

    if ((isEditMode && hasFormChanged) || (!isEditMode && hasAnyData)) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/products');
      }
    } else {
      navigate('/products');
    }
  };

  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newSupplier, setNewSupplier] = useState({ name: '', contactPerson: '', email: '', phone: '', address: '' });
  const [newBrand, setNewBrand] = useState({ 
    BrandName: '', 
    Description: '', 
    LogoUrl: '', 
    Website: '' 
  });

  const [supplierFormTouched, setSupplierFormTouched] = useState(false);
  const [brandFormTouched, setBrandFormTouched] = useState(false);

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      const response = await addCategory({
        CategoryName: newCategory.name
      });

      if (response.status === 'Success' || response) {
        const addedCategory = response.payload || response;
        toast.success('Category added successfully');
        
        const categoriesResponse = await getCategories();
        const categoriesData = categoriesResponse.payload || [];
        setCategories(categoriesData);
        
        setFormData(prev => ({ ...prev, categoryId: String(addedCategory.Id) }));
        setOpenCategoryDialog(false);
        setNewCategory({ name: '' });
      }
    } catch (error) {
      toast.error('Failed to add category');
      console.error(error);
    }
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      setSupplierFormTouched(true);
      toast.error('Supplier name is required');
      return;
    }

    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };

      const response = await addSupplier(supplierData);
      
      toast.success('Supplier added successfully');
      
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      
      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      } else {
        setSuppliers([]);
      }
      
      const addedSupplierId = response.Id || response.id || response.payload?.Id;
      if (addedSupplierId) {
        setFormData(prev => ({ ...prev, supplierId: String(addedSupplierId) }));
      }
      
      setOpenSupplierDialog(false);
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setSupplierFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding supplier:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error('Please fix the supplier form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to add supplier. Please try again.');
      }
    }
  };

  const handleAddBrand = async () => {
    if (!newBrand.BrandName.trim()) {
      setBrandFormTouched(true);
      toast.error('Brand name is required');
      return;
    }

    try {
      const brandData = {
        BrandName: newBrand.BrandName.trim(),
        Description: newBrand.Description?.trim() || null,
        LogoUrl: newBrand.LogoUrl?.trim() || null,
        Website: newBrand.Website?.trim() || null
      };

      const response = await addProductBrand(brandData);
      
      toast.success('Brand added successfully');
      
      const brandsResponse = await getProductBrands();
      const brandsData = brandsResponse?.payload || brandsResponse || [];
      
      if (Array.isArray(brandsData)) {
        setBrands(brandsData);
      } else {
        setBrands([]);
      }
      
      const addedBrandId = response.Id || response.id || response.payload?.Id;
      if (addedBrandId) {
        setFormData(prev => ({ ...prev, productBrandId: String(addedBrandId) }));
      }
      
      setOpenBrandDialog(false);
      setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
      setBrandFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding brand:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error('Please fix the brand form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to add brand. Please try again.');
      }
    }
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenCategoryDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  const handleSupplierChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenSupplierDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  const handleBrandChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenBrandDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  const categoryOptions = [
    { id: 'ADD_NEW', label: 'Add New Category', isAddNew: true },
    ...categories.map(c => ({ id: String(c.Id), label: c.CategoryName || '' })),
  ];
  const supplierOptions = [
    { id: 'ADD_NEW', label: 'Add New Supplier', isAddNew: true },
    ...suppliers.map(s => ({ id: String(s.Id), label: s.Name || s.SupplierName || 'Unknown Supplier' })),
  ];
  const brandOptions = [
    { id: 'ADD_NEW', label: 'Add New Brand', isAddNew: true },
    ...brands.map(b => ({ id: String(b.Id), label: b.BrandName || 'Unknown Brand' })),
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
      {/* ✅ Dynamic Header */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
            mb: 0.5 
          }}
        >
          {isEditMode ? t('edit_product') : t('add_product')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditMode ? t('update_product_subtitle') || 'Update the product information below' : t('add_product_subtitle') || 'Fill in the product information below'}
        </Typography>
        {/* ✅ Show data source indicator in edit mode */}
        {isEditMode && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
            {navigationData ? '📦 Data loaded from cache' : '🌐 Data loaded from server'}
          </Typography>
        )}
      </Box>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t('basic_information')}
                </Typography>
              </Box>
            </Grid>

            {/* Name - ✅ Updated helper text */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('product_name')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('name')}
                helperText={shouldShowError('name') ? errors.name : 'Max 300 characters'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 300 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('name') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('name') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* SKU */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('sku')}
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('sku')}
                helperText={shouldShowError('sku') ? errors.sku : '3-30 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ 
                  style: { textTransform: 'uppercase' },
                  maxLength: 30
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('sku') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('sku') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Barcode */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('barcode')}
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('barcode')}
                helperText={shouldShowError('barcode') ? errors.barcode : '3-30 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 30 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate barcode">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setFormData(prev => ({ ...prev, barcode: generateBarcode() }))
                          }
                          disabled={loading}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          Generate
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('barcode') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('barcode') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Description - ✅ Updated helper text and maxLength */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('description')}
                helperText={shouldShowError('description') ? errors.description : 'Max 500 characters (optional)'}
                disabled={loading}
                multiline
                rows={3}
                size="medium"
                inputProps={{ maxLength: 500 }}
                placeholder={t('description_placeholder')}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('description') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('description') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                options={categoryOptions}
                value={categoryOptions.find(o => !o.isAddNew && o.id === formData.categoryId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    handleInputChange({ target: { name: 'categoryId', value: '', type: 'text', checked: false } });
                    return;
                  }
                  if (newValue.isAddNew) { setOpenCategoryDialog(true); }
                  else { handleInputChange({ target: { name: 'categoryId', value: newValue.id, type: 'text', checked: false } }); }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="medium"
                    label="Category *"
                    error={shouldShowError('categoryId')}
                    helperText={shouldShowError('categoryId') ? errors.categoryId : undefined}
                    placeholder="Search category..."
                    onBlur={() => handleFieldBlur({ target: { name: 'categoryId' } })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
                noOptionsText="No categories available"
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>

            {/* Supplier */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                options={supplierOptions}
                value={supplierOptions.find(o => !o.isAddNew && o.id === formData.supplierId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    handleInputChange({ target: { name: 'supplierId', value: '', type: 'text', checked: false } });
                    return;
                  }
                  if (newValue.isAddNew) { setOpenSupplierDialog(true); }
                  else { handleInputChange({ target: { name: 'supplierId', value: newValue.id, type: 'text', checked: false } }); }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="medium"
                    label="Supplier *"
                    error={shouldShowError('supplierId')}
                    helperText={shouldShowError('supplierId') ? errors.supplierId : undefined}
                    placeholder="Search supplier..."
                    onBlur={() => handleFieldBlur({ target: { name: 'supplierId' } })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
                noOptionsText="No suppliers available"
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>

            {/* Brand */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                options={brandOptions}
                value={brandOptions.find(o => !o.isAddNew && o.id === formData.productBrandId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    handleInputChange({ target: { name: 'productBrandId', value: '', type: 'text', checked: false } });
                    return;
                  }
                  if (newValue.isAddNew) { setOpenBrandDialog(true); }
                  else { handleInputChange({ target: { name: 'productBrandId', value: newValue.id, type: 'text', checked: false } }); }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="medium"
                    label="Brand *"
                    error={shouldShowError('productBrandId')}
                    helperText={shouldShowError('productBrandId') ? errors.productBrandId : undefined}
                    placeholder="Search brand..."
                    onBlur={() => handleFieldBlur({ target: { name: 'productBrandId' } })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
                noOptionsText="No brands available"
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>

            {/* Model Number - ✅ Updated helper text and maxLength */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('model_number')}
                name="modelNumber"
                value={formData.modelNumber}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('modelNumber')}
                helperText={shouldShowError('modelNumber') ? errors.modelNumber : 'Max 200 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 200 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('modelNumber') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('modelNumber') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Average Price */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('average_price')}
                name="averagePrice"
                type="number"
                value={formData.averagePrice}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('averagePrice')}
                helperText={shouldShowError('averagePrice') ? errors.averagePrice : '0 - 100,000 (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('averagePrice') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('averagePrice') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Pricing & Stock Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Pricing & Stock
                </Typography>
              </Box>
            </Grid>

            {/* Cost Price */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('cost_price')}
                name="costPrice"
                type="number"
                value={formData.costPrice}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('costPrice')}
                helperText={shouldShowError('costPrice') ? errors.costPrice : '0 - 100,000'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('costPrice') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('costPrice') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Sale Price */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('sale_price')}
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('price')}
                helperText={shouldShowError('price') ? errors.price : '0 - 100,000'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('price') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('price') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Min Sale Price */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('min_sale_price')}
                name="minSalePrice"
                type="number"
                value={formData.minSalePrice}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('minSalePrice')}
                helperText={shouldShowError('minSalePrice') ? errors.minSalePrice : '0 - 100,000 (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('minSalePrice') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('minSalePrice') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Stock Quantity */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('stock_quantity')}
                name="stockQuantity"
                type="number"
                value={formData.stockQuantity}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('stockQuantity')}
                helperText={shouldShowError('stockQuantity') ? errors.stockQuantity : '0 - 100,000'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 1 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('stockQuantity') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('stockQuantity') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Min Stock Level */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('min_stock_level')}
                name="minStockLevel"
                type="number"
                value={formData.minStockLevel}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('minStockLevel')}
                helperText={shouldShowError('minStockLevel') ? errors.minStockLevel : 'Reorder threshold (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 1 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('minStockLevel') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('minStockLevel') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Additional Settings Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Additional Settings
                </Typography>
              </Box>
            </Grid>

            {/* Image URL */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('image_url')}
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('imageUrl')}
                helperText={shouldShowError('imageUrl') ? errors.imageUrl : 'Valid URL starting with http:// or https:// (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 300 }}
                placeholder="https://example.com/image.jpg"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('imageUrl') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('imageUrl') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={3}>
              <FormControl 
                fullWidth 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleInputChange}
                  label={t('status')}
                >
                  <MenuItem value={true}>{t('active')}</MenuItem>
                  <MenuItem value={false}>{t('inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Promotion Product Toggle */}
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    name="isPromotionProduct"
                    checked={formData.isPromotionProduct}
                    onChange={handleInputChange}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Promotion Product
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mark as promotional item
                    </Typography>
                  </Box>
                }
                sx={{ 
                  '& .MuiFormControlLabel-label': { ml: 1 },
                  alignItems: 'flex-start',
                  mt: 1 
                }}
              />
            </Grid>

            {/* Promotion Discount */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('promotion_discount')}
                name="promotionDiscount"
                type="number"
                value={formData.promotionDiscount}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('promotionDiscount')}
                helperText={shouldShowError('promotionDiscount') ? errors.promotionDiscount : 'Discount % (0-100)'}
                disabled={loading || !formData.isPromotionProduct}
                size="medium"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    backgroundColor: !formData.isPromotionProduct ? 'action.disabled' : 'inherit',
                    '& fieldset': {
                      borderColor: shouldShowError('promotionDiscount') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('promotionDiscount') ? 2 : 1,
                    }
                  }
                }}
                placeholder={formData.isPromotionProduct ? "Enter %" : "Enable promotion"}
              />
            </Grid>

            {/* Server Error Message */}
            {serverError && (
              <Grid item xs={12}>
                <Box
                  id="server-error-message"
                  sx={{
                    p: 2,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                    border: '1px solid #ef5350',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 500,
                      flex: 1
                    }}
                  >
                    {serverError}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setServerError('')}
                    sx={{ color: '#d32f2f' }}
                  >
                    ×
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end', 
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                {isEditMode && hasFormChanged && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResetForm}
                    disabled={loading}
                    sx={{ 
                      minWidth: 120,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Reset
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isButtonDisabled}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2,
                    opacity: isButtonDisabled ? 0.6 : 1
                  }}
                >
                  {loading 
                    ? (isEditMode ? 'Updating...' : 'Creating...') 
                    : (isEditMode ? 'Update Product' : 'Create Product')
                  }
                </Button>
              </Box>
              
              {isEditMode && !hasFormChanged && !loading && (
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Make changes to enable update
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Category Dialog */}
      <Dialog 
        open={openCategoryDialog} 
        onClose={() => setOpenCategoryDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('category_name')}
            fullWidth
            required
            value={newCategory.name}
            onChange={(e) => setNewCategory({ name: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenCategoryDialog(false);
              setNewCategory({ name: '' });
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCategory} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog 
        open={openSupplierDialog} 
        onClose={() => {
          setOpenSupplierDialog(false);
          setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
          setSupplierFormTouched(false);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Supplier</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Supplier Name *"
            fullWidth
            required
            value={newSupplier.name}
            onChange={(e) => {
              setNewSupplier({ ...newSupplier, name: e.target.value });
              if (supplierFormTouched && e.target.value.trim()) {
                setSupplierFormTouched(false);
              }
            }}
            onBlur={() => {
              if (!newSupplier.name.trim()) {
                setSupplierFormTouched(true);
              }
            }}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={supplierFormTouched && !newSupplier.name.trim() ? "Supplier name is required" : "Required field"}
            error={supplierFormTouched && !newSupplier.name.trim()}
          />
          <TextField
            margin="dense"
            label={t('contact_person')}
            fullWidth
            value={newSupplier.contactPerson || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
          <TextField
            margin="dense"
            label={t('email')}
            type="email"
            fullWidth
            value={newSupplier.email || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
          <TextField
            margin="dense"
            label={t('phone')}
            fullWidth
            value={newSupplier.phone || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
          <TextField
            margin="dense"
            label={t('address')}
            fullWidth
            multiline
            rows={2}
            value={newSupplier.address || ''}
            onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={t('optional')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenSupplierDialog(false);
              setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
              setSupplierFormTouched(false);
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddSupplier} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
            disabled={!newSupplier.name.trim()} // ✅ Disable if name is empty
          >
            Add Supplier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog 
        open={openBrandDialog} 
        onClose={() => {
          setOpenBrandDialog(false);
          setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
          setBrandFormTouched(false);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Brand</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Brand Name *"
            fullWidth
            required
            value={newBrand.BrandName}
            onChange={(e) => {
              setNewBrand({ ...newBrand, BrandName: e.target.value });
              if (brandFormTouched && e.target.value.trim()) {
                setBrandFormTouched(false);
              }
            }}
            onBlur={() => {
              if (!newBrand.BrandName.trim()) {
                setBrandFormTouched(true);
              }
            }}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText={brandFormTouched && !newBrand.BrandName.trim() ? "Brand name is required" : "Required field"}
            error={brandFormTouched && !newBrand.BrandName.trim()}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            margin="dense"
            label={t('description')}
            fullWidth
            multiline
            rows={3}
            value={newBrand.Description || ''}
            onChange={(e) => setNewBrand({ ...newBrand, Description: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText="Optional (max 500 characters)"
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            margin="dense"
            label="Logo URL"
            fullWidth
            value={newBrand.LogoUrl || ''}
            onChange={(e) => setNewBrand({ ...newBrand, LogoUrl: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText="Optional (e.g., https://example.com/logo.png)"
            placeholder="https://example.com/logo.png"
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            margin="dense"
            label="Website"
            fullWidth
            value={newBrand.Website || ''}
            onChange={(e) => setNewBrand({ ...newBrand, Website: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            helperText="Optional (e.g., https://example.com)"
            placeholder="https://example.com"
            inputProps={{ maxLength: 200 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenBrandDialog(false);
              setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
              setBrandFormTouched(false);
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddBrand} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
            disabled={!newBrand.BrandName.trim()}
          >
            Add Brand
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddProductPage;